import { queryKey } from '../constants/queryClientKey';
import {
  changePassword,
  createAccount,
  deleteAccount,
  getAccounts,
  getCurrentUser,
  getDetailAccount,
  ParamsChangePassword,
  ParamsCreateAccount,
  ParamsUpdateAccount,
  ParamsUpdateProfile,
  ParamsUpdateSSOAccount,
  updateAccount,
  updateProfile,
  updateSSOAccount,
} from '../apis/account.api';
import {
  defaultGetNextPageParam,
  sortObjectKeys,
} from '@vindr/utils/helpers/common.helper';
import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

export function useGetAccounts(
  params: Parameters<typeof getAccounts>[0],
  options?: UseQueryOptions<Awaited<ReturnType<typeof getAccounts>>>
) {
  return useQuery(
    options?.queryKey || [queryKey.account, sortObjectKeys(params)],
    () => getAccounts(params),
    options
  ) as UseQueryResult<Awaited<ReturnType<typeof getAccounts>>>;
}

export function useInfinityGetAccounts(
  params: Parameters<typeof getAccounts>[0],
  options?: UseInfiniteQueryOptions
) {
  return useInfiniteQuery(
    [queryKey.account, sortObjectKeys(params)],
    (e) =>
      getAccounts({
        ...params,
        offset: (e.pageParam || 0) + (params?.offset || 0),
      }),
    {
      getNextPageParam: defaultGetNextPageParam,
      ...options,
    }
  ) as UseInfiniteQueryResult<Awaited<ReturnType<typeof getAccounts>>>;
}

export function useGetCurrentUser(): UseQueryResult<
  ReturnType<typeof getCurrentUser>
> {
  return useQuery(['getCurrentUser'], () => getCurrentUser(), {});
}

export function useGetDetailAccount(id, config) {
  return useQuery(['getDetailAccount', id], () => getDetailAccount(id), {
    ...config,
    onSuccess: (data) => {
      config?.onSuccess?.(data.data);
    },
  }) as UseQueryResult<Awaited<ReturnType<typeof getDetailAccount>>>;
}

export function useCreateAccount() {
  const queryClient = useQueryClient();

  return useMutation(
    async (params: ParamsCreateAccount) => createAccount(params),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey.account]);
      },
      onError: (err) => {
        console.error('useCreateAccount', err);
      },
    }
  );
}

export function useUpdateAccount() {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ id, data }: { id: string; data: ParamsUpdateAccount }) =>
      updateAccount(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey.account]);
      },

      onError: (err) => {
        console.error('useUpdateAccount', err);
      },
    }
  );
}

export function useChangePassword() {
  const queryClient = useQueryClient();

  return useMutation(
    async (data: ParamsChangePassword) => changePassword(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey.userInfo]);
      },

      onError: (err) => {
        console.error('useUpdateAccount', err);
      },
    }
  );
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ id, data }: { id: string; data: ParamsUpdateProfile }) =>
      updateProfile(id, data),
    {
      mutationKey: [queryKey.update_profile],
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey.userInfo]);
      },

      onError: (err) => {
        console.error('useUpdateAccount', err);
      },
    }
  );
}

export function useUpdateSSOAccount() {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ id, data }: { id: string; data: ParamsUpdateSSOAccount }) =>
      updateSSOAccount(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey.userInfo]);
      },

      onError: (err) => {
        console.error('useUpdateSSOAccount', err);
      },
    }
  );
}

export function useDeleteAccount() {
  const queryClient = useQueryClient();

  return useMutation(async (id: string) => deleteAccount(id), {
    onSuccess: () => {
      queryClient.invalidateQueries([queryKey.account]);
    },

    onError: (err) => {
      console.error('useDeleteAccount', err);
    },
  });
}
